import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { makeCsrfFetch } from '../api';
import {
    deleteCartLine,
    fetchCart,
    postCartLine,
    putCartLine,
} from '../cart/api';
import type { Cart } from '../cart/types';

export type { Cart, CartLine, ProductImage } from '../cart/types';

const DEBOUNCE_DELAY = 500;
const ERROR_BANNER_TIMEOUT = 2000;

// Three invariants govern this module:
//   1. INTENT-DRIVEN DISPLAY: visible cart = applyIntents(serverCart, intents).
//      Click-time push, no debounce-window flicker.
//   2. RESPONSE IS TRUTH: single FIFO queue with one in-flight op, so each
//      response is unambiguously authoritative (no "older wins" race).
//   3. FAILURE REVERTS INTENT: queue resolution drops the intent; on failure
//      displayCart falls back to serverCart automatically.
//
// addToCart is non-optimistic: the line id is only known after the server responds.

type UpdateIntent = {
    id: string;
    kind: 'update';
    lineId: string;
    quantity: number;
};
type RemoveIntent = {
    id: string;
    kind: 'remove';
    lineId: string;
};
type Intent = UpdateIntent | RemoveIntent;

export type CartErrorState = { id: number; message: string };

function applyIntents(server: Cart | null, intents: readonly Intent[]): Cart | null {
    if (server === null) { return null; }
    if (intents.length === 0) { return server; }
    let lines = server.lines;
    for (const intent of intents) {
        if (intent.kind === 'remove') {
            lines = lines.filter((l) => l.id !== intent.lineId);
        } else {
            lines = lines.map((l) =>
                l.id === intent.lineId ? { ...l, quantity: intent.quantity } : l,
            );
        }
    }
    return { ...server, lines };
}

function sumQuantities(lines: readonly { quantity: number }[]): number {
    return lines.reduce((sum, l) => sum + l.quantity, 0);
}

interface CartContextValue {
    isEnabled: boolean;
    cart: Cart | null;
    cartCount: number;
    isOpen: boolean;
    isCheckoutLoading: boolean;
    cartError: CartErrorState | null;
    dismissError: () => void;
    openCart: () => void;
    closeCart: () => void;
    toggleCart: () => void;
    addToCart: (variantId: number, shopId: number, quantity?: number) => Promise<boolean>;
    updateQuantity: (lineId: string, newQuantity: number) => void;
    removeFromCart: (lineId: string) => Promise<void>;
    proceedToCheckout: () => void;
}

// Default for consumers outside a CartProvider (marketing pages that mount
// SiteNav without cart support); calls become no-ops.
const noop = () => { /* outside provider */ };
const asyncFalseNoop = async () => false;
const asyncVoidNoop = async () => { /* outside provider */ };

const DEFAULT_CONTEXT: CartContextValue = {
    isEnabled: false,
    cart: null,
    cartCount: 0,
    isOpen: false,
    isCheckoutLoading: false,
    cartError: null,
    dismissError: noop,
    openCart: noop,
    closeCart: noop,
    toggleCart: noop,
    addToCart: asyncFalseNoop,
    updateQuantity: noop,
    removeFromCart: asyncVoidNoop,
    proceedToCheckout: noop,
};

const CartContext = createContext<CartContextValue>(DEFAULT_CONTEXT);

export function useCart(): CartContextValue {
    return useContext(CartContext);
}

function readCsrfTokenFromPageData(): string | null {
    const el = document.getElementById('pageData');
    if (!el || !el.textContent) { return null; }
    try {
        const data = JSON.parse(el.textContent);
        if (typeof data?.csrfToken === 'string' && data.csrfToken.length > 0) {
            return data.csrfToken;
        }
    } catch {
        return null;
    }
    return null;
}

type CartProviderProps = {
    children?: React.ReactNode;
    csrfToken?: string;
};

export function CartProvider({ children, csrfToken: csrfTokenProp }: CartProviderProps) {
    const csrfToken = useMemo(
        () => csrfTokenProp ?? readCsrfTokenFromPageData(),
        [csrfTokenProp],
    );
    const isEnabled = csrfToken !== null;
    const csrfFetch = useMemo(
        () => (csrfToken !== null ? makeCsrfFetch(csrfToken) : null),
        [csrfToken],
    );

    // === Authoritative state ===
    const [serverCart, setServerCart] = useState<Cart | null>(null);
    const [intents, setIntents] = useState<Intent[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    // Tracks "cart is mid-mutation": a queue op is in flight or a debounce
    // timer is pending. Exposed isCheckoutLoading also folds in the
    // checkout-url-not-yet-provisioned case below.
    const [isMutating, setIsMutating] = useState(false);
    const [cartError, setCartError] = useState<CartErrorState | null>(null);

    // === Refs ARE the canonical mutable state ===
    // useEffect-based mirroring would lag a render cycle: a queued op resolving
    // setServerCart, then the next op running in the microtask before React
    // commits, would observe a stale ref. We write to ref and state together
    // via the helpers below so refs are always authoritative for queued ops.
    const serverCartRef = useRef<Cart | null>(null);
    const intentsRef = useRef<Intent[]>([]);

    const writeServerCart = useCallback((next: Cart | null) => {
        serverCartRef.current = next;
        setServerCart(next);
    }, []);

    const writeIntents = useCallback((updater: (prev: Intent[]) => Intent[]) => {
        const next = updater(intentsRef.current);
        intentsRef.current = next;
        setIntents(next);
    }, []);

    // === FIFO queue for every server mutation ===
    const queueRef = useRef<Promise<unknown>>(Promise.resolve());
    const queueDepthRef = useRef(0);

    // === Debounce machinery: at most one open update intent per line ===
    const debounceTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    const openUpdateIntentRef = useRef<Map<string, string>>(new Map());

    // === Misc ===
    const intentIdCounterRef = useRef(0);
    const errorIdCounterRef = useRef(0);
    const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const nextIntentId = useCallback((): string => {
        intentIdCounterRef.current += 1;
        return `i${intentIdCounterRef.current}`;
    }, []);

    const refreshLoading = useCallback(() => {
        setIsMutating(
            queueDepthRef.current > 0 || debounceTimersRef.current.size > 0,
        );
    }, []);

    // Enqueue a server op behind every prior op. The returned promise resolves
    // when this op finishes; callers can await to chain UI decisions.
    const enqueue = useCallback(<T,>(op: () => Promise<T>): Promise<T> => {
        queueDepthRef.current += 1;
        refreshLoading();
        const run = async (): Promise<T> => {
            try {
                return await op();
            } finally {
                queueDepthRef.current -= 1;
                refreshLoading();
            }
        };
        const next = queueRef.current.then(run, run);
        // Swallow rejections on the chain so a single failure doesn't poison
        // subsequent ops. Callers still see the rejection via the returned
        // promise.
        queueRef.current = next.catch(() => undefined);
        return next;
    }, [refreshLoading]);

    // === Error banner ===
    const clearErrorTimer = useCallback(() => {
        if (errorTimerRef.current !== null) {
            clearTimeout(errorTimerRef.current);
            errorTimerRef.current = null;
        }
    }, []);

    const dismissError = useCallback(() => {
        clearErrorTimer();
        setCartError(null);
    }, [clearErrorTimer]);

    // Surface a cart-op error inside the panel. The id field lets identical
    // messages remount the banner so the countdown animation restarts.
    const showError = useCallback((
        message: string,
        opts?: { openPanel?: boolean },
    ) => {
        clearErrorTimer();
        errorIdCounterRef.current += 1;
        setCartError({ id: errorIdCounterRef.current, message });
        if (opts?.openPanel !== false) {
            setIsOpen(true);
        }
        errorTimerRef.current = setTimeout(() => {
            setCartError(null);
            errorTimerRef.current = null;
        }, ERROR_BANNER_TIMEOUT);
    }, [clearErrorTimer]);

    // === Cleanup on unmount ===
    useEffect(() => {
        const timers = debounceTimersRef.current;
        return () => {
            timers.forEach((t) => clearTimeout(t));
            timers.clear();
            clearErrorTimer();
        };
    }, [clearErrorTimer]);

    // === Initial cart load: also goes through the queue so mutations made
    //     before the first GET resolves still see a consistent serverCart. ===
    useEffect(() => {
        if (csrfFetch === null) { return; }
        let cancelled = false;
        void enqueue(async () => {
            if (cancelled) { return; }
            const result = await fetchCart(csrfFetch);
            if (cancelled) { return; }
            if (result.kind === 'ok') {
                writeServerCart(result.cart);
            } else if (result.kind === 'empty') {
                writeServerCart(null);
            } else {
                showError('Something went wrong, please try again later', { openPanel: false });
            }
        });
        return () => { cancelled = true; };
    }, [csrfFetch, enqueue, showError]);

    // === Derived display ===
    const displayCart = useMemo(
        () => applyIntents(serverCart, intents),
        [serverCart, intents],
    );
    const cartCount = useMemo(
        () => (displayCart === null ? 0 : sumQuantities(displayCart.lines)),
        [displayCart],
    );

    // === UI controls ===
    const openCart = useCallback(() => { setIsOpen(true); }, []);
    const closeCart = useCallback(() => {
        setIsOpen(false);
        // Clear any banner so reopening within the timer doesn't show stale.
        clearErrorTimer();
        setCartError(null);
    }, [clearErrorTimer]);
    const toggleCart = useCallback(() => { setIsOpen((v) => !v); }, []);

    // === commitOpenIntent: timer-fire path; also used by checkout flush ===
    // Marks the line's pending update intent as committed (no further qty
    // mutations) and enqueues the PUT. The op reads the intent's latest qty
    // at dispatch time, drops the intent on resolution, and applies the
    // server cart authoritatively.
    const commitOpenIntent = useCallback((lineId: string) => {
        if (csrfFetch === null) { return; }
        const timer = debounceTimersRef.current.get(lineId);
        if (timer !== undefined) {
            clearTimeout(timer);
            debounceTimersRef.current.delete(lineId);
        }
        const intentId = openUpdateIntentRef.current.get(lineId);
        openUpdateIntentRef.current.delete(lineId);
        if (intentId === undefined) {
            refreshLoading();
            return;
        }
        void enqueue(async () => {
            const intent = intentsRef.current.find((i) => i.id === intentId);
            if (intent === undefined || intent.kind !== 'update') { return; }
            const targetQty = intent.quantity;
            const result = await putCartLine(csrfFetch, {
                line_id: lineId,
                quantity: targetQty,
            });
            // Drop the intent first so display falls back to serverCart on
            // failure paths.
            writeIntents((prev) => prev.filter((i) => i.id !== intentId));
            if (result.kind === 'gone') {
                showError('Your cart is no longer available. Please add items again.');
                writeServerCart(null);
                return;
            }
            if (result.kind === 'error') {
                showError('Something went wrong, please try again later');
                return;
            }
            writeServerCart(result.cart);
            const updatedLine = result.cart.lines.find((l) => l.id === lineId);
            if (updatedLine !== undefined && updatedLine.quantity !== targetQty) {
                showError(`Quantity adjusted to ${updatedLine.quantity} (limited stock)`);
            }
        });
    }, [csrfFetch, enqueue, refreshLoading, showError]);

    // Forward-reference removeFromCart for updateQuantity (mutual recursion
    // resolved via ref to avoid useCallback dep cycles).
    const removeFromCartRef = useRef<(lineId: string) => Promise<void>>(asyncVoidNoop);

    const updateQuantity = useCallback((lineId: string, newQuantity: number) => {
        if (csrfFetch === null) { return; }
        if (newQuantity <= 0) {
            void removeFromCartRef.current(lineId);
            return;
        }

        const oldTimer = debounceTimersRef.current.get(lineId);
        if (oldTimer !== undefined) { clearTimeout(oldTimer); }

        // If there's already an open intent for this line, mutate its qty in
        // place (keeps the same intent id so any queued op resolves it
        // correctly). Otherwise create a new one.
        const existingIntentId = openUpdateIntentRef.current.get(lineId);
        if (existingIntentId !== undefined) {
            const targetId = existingIntentId;
            writeIntents((prev) => prev.map((i) =>
                (i.id === targetId && i.kind === 'update')
                    ? { ...i, quantity: newQuantity }
                    : i,
            ));
        } else {
            const newIntentId = nextIntentId();
            openUpdateIntentRef.current.set(lineId, newIntentId);
            writeIntents((prev) => [
                ...prev,
                { id: newIntentId, kind: 'update', lineId, quantity: newQuantity },
            ]);
        }

        const newTimer = setTimeout(() => {
            commitOpenIntent(lineId);
        }, DEBOUNCE_DELAY);
        debounceTimersRef.current.set(lineId, newTimer);
        refreshLoading();
    }, [csrfFetch, nextIntentId, commitOpenIntent, refreshLoading]);

    const removeFromCart = useCallback(async (lineId: string): Promise<void> => {
        if (csrfFetch === null) { return; }

        // Cancel any open update intent for this line; the remove subsumes it.
        const oldTimer = debounceTimersRef.current.get(lineId);
        if (oldTimer !== undefined) { clearTimeout(oldTimer); }
        debounceTimersRef.current.delete(lineId);
        const oldOpenId = openUpdateIntentRef.current.get(lineId);
        openUpdateIntentRef.current.delete(lineId);

        const removeIntentId = nextIntentId();
        writeIntents((prev) => {
            const filtered = oldOpenId !== undefined
                ? prev.filter((i) => i.id !== oldOpenId)
                : prev;
            return [...filtered, { id: removeIntentId, kind: 'remove', lineId }];
        });

        await enqueue(async () => {
            const result = await deleteCartLine(csrfFetch, { line_id: lineId });
            writeIntents((prev) => prev.filter((i) => i.id !== removeIntentId));
            if (result.kind === 'gone') {
                showError('Your cart is no longer available. Please add items again.');
                writeServerCart(null);
                return;
            }
            if (result.kind === 'error') {
                showError('Something went wrong, please try again later');
                return;
            }
            writeServerCart(result.cart);
        });
    }, [csrfFetch, enqueue, nextIntentId, showError]);

    useEffect(() => { removeFromCartRef.current = removeFromCart; }, [removeFromCart]);

    // Add-to-cart is non-optimistic: the new line id is unknown until the
    // server responds, so we just enqueue the POST and apply the result.
    const addToCart = useCallback(async (
        variantId: number,
        shopId: number,
        quantity: number = 1,
    ): Promise<boolean> => {
        if (csrfFetch === null) { return false; }
        if (!variantId) {
            showError('Please select a variant');
            return false;
        }

        return enqueue(async () => {
            const result = await postCartLine(csrfFetch, {
                variant_id: variantId,
                quantity,
                shop_id: shopId,
            });
            if (result.kind !== 'ok') {
                showError('Something went wrong, please try again later');
                return false;
            }
            writeServerCart(result.cart);
            // Server reports the exact quantity Shopify accepted; a partial
            // fulfillment means Shopify capped at available stock.
            if (result.addedQuantity < quantity) {
                showError('Added to cart (limited quantity)');
            } else {
                dismissError();
            }
            return true;
        });
    }, [csrfFetch, enqueue, showError, dismissError]);

    // Checkout: drain any open debounces into the queue, then enqueue the
    // navigate. Because the queue is FIFO with a single in-flight slot, the
    // navigation only fires after every committed mutation has resolved.
    const proceedToCheckout = useCallback(() => {
        if (csrfFetch === null) { return; }
        for (const lineId of [...debounceTimersRef.current.keys()]) {
            commitOpenIntent(lineId);
        }
        void enqueue(async () => {
            const cart = serverCartRef.current;
            if (cart === null || cart.lines.length === 0) { return; }
            if (cart.checkout_url === null) { return; }
            window.location.href = cart.checkout_url;
        });
    }, [csrfFetch, enqueue, commitOpenIntent]);

    // Folds the not-yet-provisioned checkout URL case into the loading
    // signal so the button can show its preparing/shimmer state during
    // init as well as during in-flight mutations.
    const isCheckoutLoading = isMutating || serverCart?.checkout_url === null;

    const value = useMemo<CartContextValue>(() => ({
        isEnabled,
        cart: displayCart,
        cartCount,
        isOpen,
        isCheckoutLoading,
        cartError,
        dismissError,
        openCart,
        closeCart,
        toggleCart,
        addToCart,
        updateQuantity,
        removeFromCart,
        proceedToCheckout,
    }), [
        isEnabled,
        displayCart,
        cartCount,
        isOpen,
        isCheckoutLoading,
        cartError,
        dismissError,
        openCart,
        closeCart,
        toggleCart,
        addToCart,
        updateQuantity,
        removeFromCart,
        proceedToCheckout,
    ]);

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
}
